import type {
  FacetableFieldsDTO,
  FacetableValuesDTO,
  HttpService,
} from '@empathyco/playboard-core-domain'
import type { BrowseFacetAdapter } from '../adapter/interfaces'
import type { PlatformBrowseFacetEntity } from '../adapter/platform-browse-facet.adapter'
import type { BrowseFacetDTO } from '../models'
import type { ContextProvider } from '../providers/interfaces'
import type { BrowseFacetService, BrowseFacetServiceListParams } from './interfaces'
import { interpolate } from '@empathyco/x-adapter'
import { inject, injectable, named, optional } from 'inversify'
import { isNonSortableFacetType } from '../composables/use-facets'
import { TYPES } from '../config/inversify.types'
import { FacetSortTypeEnum, FacetTypeEnum } from '../enums'

const FACET_TYPE_BY_FIELD_KEY: Record<keyof FacetableFieldsDTO, FacetTypeEnum> = {
  hierarchicalFacets: FacetTypeEnum.HIERARCHICAL,
  valueFacets: FacetTypeEnum.VALUE,
  rangeFacets: FacetTypeEnum.RANGE,
  booleanFacets: FacetTypeEnum.BOOLEAN,
  sliderFacets: FacetTypeEnum.SLIDER,
}

@injectable()
export class PlatformBrowseFacetService implements BrowseFacetService {
  public constructor(
    @inject(TYPES.HttpService) protected httpService: HttpService<PlatformBrowseFacetEntity>,
    @inject(TYPES.BrowseFacetAdapter)
    protected adapter: BrowseFacetAdapter<PlatformBrowseFacetEntity | undefined>,
    @inject(TYPES.FacetsUrl) protected url: string,
    @inject(TYPES.ContextProvider) protected contextProvider: ContextProvider,
    @inject(TYPES.Endpoint)
    @named('browseFacet')
    @optional()
    protected endpoint = '/query/{instance}/facet-values',
  ) {}

  async list(params: BrowseFacetServiceListParams): Promise<Array<BrowseFacetDTO>> {
    try {
      let extraParams: { [key: string]: any } = {}
      if (params.context === 'search' && params.query && params.query.length > 0) {
        extraParams = {
          context: params.context?.toUpperCase() ?? 'SEARCH',
          query: params.query,
        }
      } else if (!params.field || !params.value) {
        extraParams = {
          context: 'NONE',
        }
      } else {
        extraParams = {
          browseField: params.field,
          browseValue: params.value,
          context: params.context?.toUpperCase() ?? 'BROWSE',
        }
      }

      const facets: string[] = []

      if (params.facetableFields) {
        Object.entries(params.facetableFields).forEach(([facetKey, facetItems]) => {
          if (!facetItems?.length) {
            return
          }

          const facetType = FACET_TYPE_BY_FIELD_KEY[facetKey as keyof FacetableFieldsDTO]

          facetItems.forEach((facetItem: FacetableValuesDTO) => {
            facets.push(facetItem.field)

            if (isNonSortableFacetType(facetType)) {
              return
            }

            const sortableFacetItem = facetItem as FacetableValuesDTO
            const sortType: string = sortableFacetItem.sortType
              ? sortableFacetItem.sortType === FacetSortTypeEnum.CUSTOM
                ? ''
                : sortableFacetItem.sortType
              : FacetSortTypeEnum.ALPHABETICALLY

            extraParams[`f.${facetItem.field}.sort`] = sortType

            if (sortableFacetItem.rankings) {
              const rankingsString = sortableFacetItem.rankings
                .map(item => item.value)
                .filter(value => value != null)
                .join(',')

              if (rankingsString) {
                extraParams[`f.${facetItem.field}.boosts`] = rankingsString
              }
            }
          })
        })

        if (facets.length > 0) {
          ;(extraParams as any).facet = facets
        }
      }

      const response = await this.httpService.post<{
        catalog: { content: []; facets: Array<PlatformBrowseFacetEntity> }
      }>(this.buildRequestUrl(), {
        addAccessToken: true,
        body: {
          ...extraParams,
          ...params.pivots.reduce<Record<string, string>>(
            (result, pivot) => ({ ...result, [pivot.name]: pivot.values[0] }),
            {},
          ),
        },
      })
      return response.data?.catalog.facets?.map(facet => this.adapter.decode(facet)) ?? []
    } catch (error) {
      throw this.adapter.decodeError(error)
    }
  }

  private buildRequestUrl(): string {
    const url = `${this.url}${this.endpoint}`
    const { instanceId: instance } = this.contextProvider()
    return interpolate(url, { instance })
  }
}
